import type { Element, ElementContent, Root, RootContent } from "hast";

type Parent = Root | Element;

const walk = (node: Parent, visit: (element: Element) => void) => {
  for (const child of node.children as (RootContent | ElementContent)[]) {
    if (child.type === "element") {
      visit(child);
      walk(child, visit);
    }
  }
};

/**
 * Gives `h2`–`h4` the anchors the server computed, by position. The server's heading list is the one the content
 * tests validate links against, so taking ids from it (rather than slugging again here) means a link that passes the
 * tests is a link that lands.
 */
export const rehypeHeadingIds = (anchors: string[]) => (tree: Root) => {
  let index = 0;
  walk(tree, (element) => {
    if (element.tagName === "h2" || element.tagName === "h3" || element.tagName === "h4") {
      const anchor = anchors[index++];
      if (anchor) element.properties = { ...element.properties, id: anchor };
    }
  });
};

/**
 * Numbers the page's ```diagram blocks in document order (`data-diagram-index`), matching the order of the SVGs the
 * server rendered for them.
 */
export const rehypeDiagramIndex = () => (tree: Root) => {
  let index = 0;
  walk(tree, (element) => {
    if (element.tagName !== "pre") return;
    const code = element.children.find((c): c is Element => c.type === "element" && c.tagName === "code");
    const classes = (code?.properties?.className as string[] | undefined) ?? [];
    if (classes.includes("language-diagram")) element.properties = { ...element.properties, dataDiagramIndex: index++ };
  });
};

export type AlertType = "note" | "tip" | "important" | "warning" | "caution";

const ALERT_MARKER = /^\s*\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*/i;

/**
 * GitHub alert syntax — a blockquote whose first line is `[!WARNING]` — marked with `data-alert` and the marker
 * removed, so the blockquote renderer can draw a callout.
 */
export const rehypeAlerts = () => (tree: Root) => {
  walk(tree, (element) => {
    if (element.tagName !== "blockquote") return;
    const paragraph = element.children.find((c): c is Element => c.type === "element");
    if (!paragraph || paragraph.tagName !== "p") return;
    const first = paragraph.children[0];
    if (!first || first.type !== "text") return;
    const match = ALERT_MARKER.exec(first.value);
    if (!match) return;
    first.value = first.value.slice(match[0].length);
    if (first.value === "") paragraph.children.shift();
    if (paragraph.children.length === 0) element.children.splice(element.children.indexOf(paragraph), 1);
    element.properties = { ...element.properties, dataAlert: match[1].toLowerCase() };
  });
};
